import React,{useState,useRef,useEffect,useCallback} from 'react';
import {X,Printer,QrCode,BarChart2,Tag,Package,ChevronDown,ChevronUp,Plus,Minus} from 'lucide-react';
import {QRCodeCanvas} from 'qrcode.react';

/* ── Code128 barcode renderer (pure canvas, no lib) ──────────── */
const CODE128_MAP={
  ' ':0,'!':1,'"':2,'#':3,'$':4,'%':5,'&':6,"'":7,'(':8,')':9,'*':10,
  '+':11,',':12,'-':13,'.':14,'/':15,'0':16,'1':17,'2':18,'3':19,'4':20,
  '5':21,'6':22,'7':23,'8':24,'9':25,':':26,';':27,'<':28,'=':29,'>':30,
  '?':31,'@':32,'A':33,'B':34,'C':35,'D':36,'E':37,'F':38,'G':39,'H':40,
  'I':41,'J':42,'K':43,'L':44,'M':45,'N':46,'O':47,'P':48,'Q':49,'R':50,
  'S':51,'T':52,'U':53,'V':54,'W':55,'X':56,'Y':57,'Z':58,'[':59,'\\':60,
  ']':61,'^':62,'_':63,'`':64,'a':65,'b':66,'c':67,'d':68,'e':69,'f':70,
  'g':71,'h':72,'i':73,'j':74,'k':75,'l':76,'m':77,'n':78,'o':79,'p':80,
  'q':81,'r':82,'s':83,'t':84,'u':85,'v':86,'w':87,'x':88,'y':89,'z':90,
  '{':91,'|':92,'}':93,'~':94,
};
const CODE128_PATTERNS=[
  '11011001100','11001101100','11001100110','10010011000','10010001100',
  '10001001100','10011001000','10011000100','10001100100','11001001000',
  '11001000100','11000100100','10110011100','10011011100','10011001110',
  '10111001100','10011101100','10011100110','11001110010','11001011100',
  '11001001110','11011100100','11001110100','11101101110','11101001100',
  '11100101100','11100100110','11101100100','11100110100','11100110010',
  '11011011000','11011000110','11000110110','10100011000','10001011000',
  '10001000110','10110001000','10001101000','10001100010','11010001000',
  '11000101000','11000100010','10110111000','10110001110','10001101110',
  '10111011000','10111000110','10001110110','11101110110','11010001110',
  '11000101110','11011101000','11011100010','11011101110','11101011000',
  '11101000110','11100010110','11101101000','11101100010','11100011010',
  '11101111010','11001000010','11110001010','10100110000','10100001100',
  '10010110000','10010000110','10000101100','10000100110','10110010000',
  '10110000100','10011010000','10011000010','10000110100','10000110010',
  '11000010010','11001010000','11110111010','11000010100','10001111010',
  '10100111100','10010111100','10010011110','10111100100','10011110100',
  '10011110010','11110100100','11110010100','11110010010','11011011110',
  '11011110110','11110110110','10101111000','10100011110','10001011110',
  '10111101000','10111100010','11110101000','11110100010','10111011110',
  '10111101110','11101011110','11110101110','11010000100','11010010000',
  '11010011100','1100011101011',
];
function drawBarcode(canvas,text,opts={}){
  const{barWidth=2,height=60,color='#000',bg='#fff'}=opts;
  if(!canvas||!text)return;
  const chars=[...text];
  const vals=chars.map(c=>CODE128_MAP[c]??-1);
  if(vals.some(v=>v<0)){
    const ctx=canvas.getContext('2d');
    canvas.width=200;canvas.height=height;
    ctx.fillStyle=bg;ctx.fillRect(0,0,200,height);
    ctx.fillStyle=color;ctx.font='12px monospace';
    ctx.fillText('Invalid barcode chars',4,height/2);
    return;
  }
  const startVal=104;
  const checksum=(startVal+vals.reduce((s,v,i)=>s+v*(i+1),0))%103;
  const bars=[
    CODE128_PATTERNS[startVal],
    ...vals.map(v=>CODE128_PATTERNS[v]),
    CODE128_PATTERNS[checksum],
    CODE128_PATTERNS[106],
  ];
  const totalBits=bars.join('').length;
  // 2× pixel density for crisp print output
  const scale=2;
  const W=(totalBits*barWidth+20)*scale;
  const H=height*scale;
  canvas.width=W;
  canvas.height=H;
  canvas.style.width=`${totalBits*barWidth+20}px`;
  canvas.style.height=`${height}px`;
  const ctx=canvas.getContext('2d');
  ctx.scale(scale,scale);
  ctx.fillStyle=bg;ctx.fillRect(0,0,W,H);
  ctx.fillStyle=color;
  let x=10;
  for(const bar of bars){
    for(const bit of bar){
      if(bit==='1') ctx.fillRect(x,0,barWidth,height-16);
      x+=barWidth;
    }
  }
  ctx.fillStyle=color;ctx.font=`11px monospace`;ctx.textAlign='center';
  ctx.fillText(text,(totalBits*barWidth+20)/2,height-2);
}

/* ── Single Label Component ──────────────────────────────────── */
function Label({product,opts,size}){
  const barcodeRef=useRef(null);
  const{showBarcode,showQR,showName,showNameKh,showSku,showPrice,codeType,qrValue}=opts;
  const{w,h}=size;

  const barcodeVal=product.barcode||product.sku||'';
  const qrVal=qrValue==='barcode'?(product.barcode||product.sku||'')
    :qrValue==='sku'?product.sku||''
    :qrValue==='name'?product.name||''
    :`${product.name||''} | ${product.sku||''} | $${product.sale_price||0}`;

  useEffect(()=>{
    if(showBarcode&&codeType==='barcode'&&barcodeRef.current&&barcodeVal){
      drawBarcode(barcodeRef.current,barcodeVal,{barWidth:1.5,height:45});
    }
  },[showBarcode,codeType,barcodeVal]);

  return(
    <div className="product-label" style={{
      width:w,height:h,border:'1px solid #ccc',borderRadius:4,
      padding:'6px 8px',background:'#fff',color:'#000',
      fontFamily:'Arial,sans-serif',display:'flex',flexDirection:'column',
      alignItems:'center',justifyContent:'center',gap:2,
      pageBreakInside:'avoid',boxSizing:'border-box',overflow:'hidden',
    }}>
      {/* Name */}
      {showName&&<div style={{fontSize:11,fontWeight:700,textAlign:'center',lineHeight:1.2,maxWidth:'100%',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{product.name}</div>}
      {showNameKh&&product.name_kh&&<div style={{fontSize:10,color:'#555',textAlign:'center',lineHeight:1.2}}>{product.name_kh}</div>}

      {/* SKU */}
      {showSku&&product.sku&&<div style={{fontSize:9,color:'#777',fontFamily:'monospace'}}>{product.sku}</div>}

      {/* Barcode */}
      {showBarcode&&codeType==='barcode'&&barcodeVal&&(
        <canvas ref={barcodeRef} style={{maxWidth:'100%',height:45}}/>
      )}

      {/* QR Code */}
      {showBarcode&&codeType==='qr'&&qrVal&&(
        <QRCodeCanvas value={qrVal} size={Math.min(w*0.55,65)} level="M" includeMargin={false}/>
      )}

      {/* Price */}
      {showPrice&&<div style={{fontSize:14,fontWeight:900,color:'#000'}}>${parseFloat(product.sale_price||0).toFixed(2)}</div>}
    </div>
  );
}

/* ── Main Modal ──────────────────────────────────────────────── */
const LABEL_SIZES={
  '50x30':{label:'50×30 mm',w:189,h:113},
  '60x40':{label:'60×40 mm',w:227,h:151},
  '70x40':{label:'70×40 mm',w:264,h:151},
  '100x50':{label:'100×50 mm',w:378,h:189},
  '100x100':{label:'100×100 mm (square)',w:378,h:378},
};
const QR_VALUES=[
  {value:'barcode',label:'Barcode / Code value'},
  {value:'sku',label:'SKU only'},
  {value:'name',label:'Product name'},
  {value:'all',label:'Name + SKU + Price'},
];

export default function ProductLabelModal({products=[],onClose}){
  const[selected,setSelected]=useState(()=>new Set([products[0]?.id].filter(Boolean)));
  const[labelSize,setLabelSize]=useState('60x40');
  const[cols,setCols]=useState(3);
  const[copies,setCopies]=useState(1);
  const[opts,setOpts]=useState({
    showName:true,showNameKh:true,showSku:true,
    showPrice:true,showBarcode:true,
    codeType:'barcode', // 'barcode' | 'qr'
    qrValue:'barcode',
  });
  const previewRef=useRef(null);

  const toggle=k=>setOpts(o=>({...o,[k]:!o[k]}));
  const size=LABEL_SIZES[labelSize];

  const selectedProducts=products.filter(p=>selected.has(p.id));
  const labelItems=[];
  for(const p of selectedProducts){
    for(let i=0;i<copies;i++) labelItems.push(p);
  }

  const handlePrint=()=>{
    const el=previewRef.current;
    if(!el)return;

    // Clone the DOM so we can mutate it without affecting the preview
    const clone=el.cloneNode(true);

    // Replace every <canvas> in the clone with an <img> carrying its pixel data
    const origCanvases=el.querySelectorAll('canvas');
    const cloneCanvases=clone.querySelectorAll('canvas');
    origCanvases.forEach((c,i)=>{
      try{
        const dataUrl=c.toDataURL('image/png');
        const img=document.createElement('img');
        img.src=dataUrl;
        img.style.cssText=c.style.cssText||'';
        img.style.maxWidth='100%';
        img.style.display='block';
        cloneCanvases[i]?.parentNode?.replaceChild(img,cloneCanvases[i]);
      }catch(e){console.warn('canvas toDataURL failed',e);}
    });

    const html=`<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Product Labels</title>
<style>
*{margin:0;padding:0;box-sizing:border-box}
body{background:#fff;font-family:Arial,sans-serif}
.label-grid{display:grid;grid-template-columns:repeat(${cols},auto);gap:4mm;padding:5mm;justify-content:start}
.product-label{border:1px solid #ccc;border-radius:3px;padding:4px 6px;background:#fff;color:#000;
  display:flex;flex-direction:column;align-items:center;justify-content:center;gap:2px;
  page-break-inside:avoid;overflow:hidden}
img{max-width:100%;display:block}
@page{margin:8mm}
@media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
</style></head>
<body><div class="label-grid">${clone.innerHTML}</div></body></html>`;
    const pw=window.open('','_blank','width=900,height=700');
    if(!pw){alert('Please allow popups to print');return;}
    pw.document.write(html);
    pw.document.close();
    pw.focus();
    setTimeout(()=>{pw.print();pw.close();},800);
  };

  const Opt=({label,k})=>(
    <label style={{display:'flex',alignItems:'center',gap:7,cursor:'pointer',fontSize:13,userSelect:'none'}}>
      <div
        onClick={()=>toggle(k)}
        style={{width:18,height:18,borderRadius:4,border:`2px solid ${opts[k]?'var(--primary)':'var(--border)'}`,
          background:opts[k]?'var(--primary)':'transparent',display:'flex',alignItems:'center',
          justifyContent:'center',flexShrink:0,cursor:'pointer',transition:'all .15s'}}>
        {opts[k]&&<span style={{color:'#fff',fontSize:11,fontWeight:900}}>✓</span>}
      </div>
      {label}
    </label>
  );

  return(
    <div onClick={e=>{if(e.target===e.currentTarget)onClose();}}
      style={{position:'fixed',inset:0,zIndex:2000,background:'rgba(0,0,0,.7)',backdropFilter:'blur(3px)',
        display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
      <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:16,
        width:'100%',maxWidth:960,maxHeight:'94vh',display:'flex',flexDirection:'column',overflow:'hidden'}}>

        {/* Header */}
        <div style={{padding:'14px 20px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',justifyContent:'space-between',flexShrink:0}}>
          <div style={{display:'flex',alignItems:'center',gap:8}}>
            <Tag size={18} color="var(--primary)"/>
            <span style={{fontWeight:700,fontSize:15}}>Print Product Labels / បោះពុម្ពស្លាកផលិតផល</span>
          </div>
          <div style={{display:'flex',gap:8}}>
            <button className="btn btn-primary btn-sm" onClick={handlePrint} style={{gap:5}}>
              <Printer size={13}/> Print Labels
            </button>
            <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',color:'var(--td)',display:'flex',borderRadius:6,padding:4}}>
              <X size={18}/>
            </button>
          </div>
        </div>

        <div style={{flex:1,display:'grid',gridTemplateColumns:'280px 1fr',overflow:'hidden'}}>

          {/* ── LEFT: Settings panel ── */}
          <div style={{borderRight:'1px solid var(--border)',overflowY:'auto',padding:'16px'}}>

            {/* Product selector */}
            <div style={{marginBottom:16}}>
              <div style={{fontSize:11,fontWeight:700,color:'var(--td)',textTransform:'uppercase',letterSpacing:.6,marginBottom:8}}>
                Products ({selected.size} selected)
              </div>
              <div style={{maxHeight:140,overflowY:'auto',border:'1px solid var(--border)',borderRadius:8}}>
                {products.map(p=>(
                  <div key={p.id} onClick={()=>setSelected(s=>{const n=new Set(s);n.has(p.id)?n.delete(p.id):n.add(p.id);return n;})}
                    style={{display:'flex',alignItems:'center',gap:8,padding:'7px 10px',cursor:'pointer',
                      background:selected.has(p.id)?'var(--pm)':'transparent',transition:'background .1s',
                      borderBottom:'1px solid var(--border)'}}>
                    <div style={{width:14,height:14,borderRadius:3,border:`2px solid ${selected.has(p.id)?'var(--primary)':'var(--border)'}`,
                      background:selected.has(p.id)?'var(--primary)':'transparent',flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center'}}>
                      {selected.has(p.id)&&<span style={{color:'#fff',fontSize:9,fontWeight:900}}>✓</span>}
                    </div>
                    <div style={{minWidth:0}}>
                      <div style={{fontSize:12,fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{p.name}</div>
                      <div style={{fontSize:10,color:'var(--td)',fontFamily:'monospace'}}>{p.sku} · ${p.sale_price}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{display:'flex',gap:6,marginTop:6}}>
                <button className="btn btn-ghost btn-sm" style={{flex:1,fontSize:11}} onClick={()=>setSelected(new Set(products.map(p=>p.id)))}>All</button>
                <button className="btn btn-ghost btn-sm" style={{flex:1,fontSize:11}} onClick={()=>setSelected(new Set())}>None</button>
              </div>
            </div>

            {/* Label size */}
            <div style={{marginBottom:14}}>
              <div style={{fontSize:11,fontWeight:700,color:'var(--td)',textTransform:'uppercase',letterSpacing:.6,marginBottom:8}}>Label Size</div>
              <select value={labelSize} onChange={e=>setLabelSize(e.target.value)}
                style={{width:'100%',padding:'8px 10px',borderRadius:8,border:'1px solid var(--border)',background:'var(--s2)',color:'var(--text)',fontSize:12}}>
                {Object.entries(LABEL_SIZES).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>

            {/* Copies + columns */}
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:14}}>
              <div>
                <div style={{fontSize:11,fontWeight:700,color:'var(--td)',textTransform:'uppercase',letterSpacing:.6,marginBottom:6}}>Copies</div>
                <div style={{display:'flex',alignItems:'center',gap:6}}>
                  <button className="btn btn-ghost btn-sm" style={{padding:'4px 8px'}} onClick={()=>setCopies(c=>Math.max(1,c-1))}><Minus size={12}/></button>
                  <span style={{fontWeight:700,minWidth:24,textAlign:'center'}}>{copies}</span>
                  <button className="btn btn-ghost btn-sm" style={{padding:'4px 8px'}} onClick={()=>setCopies(c=>Math.min(20,c+1))}><Plus size={12}/></button>
                </div>
              </div>
              <div>
                <div style={{fontSize:11,fontWeight:700,color:'var(--td)',textTransform:'uppercase',letterSpacing:.6,marginBottom:6}}>Per Row</div>
                <div style={{display:'flex',alignItems:'center',gap:6}}>
                  <button className="btn btn-ghost btn-sm" style={{padding:'4px 8px'}} onClick={()=>setCols(c=>Math.max(1,c-1))}><Minus size={12}/></button>
                  <span style={{fontWeight:700,minWidth:24,textAlign:'center'}}>{cols}</span>
                  <button className="btn btn-ghost btn-sm" style={{padding:'4px 8px'}} onClick={()=>setCols(c=>Math.min(6,c+1))}><Plus size={12}/></button>
                </div>
              </div>
            </div>

            {/* Code type toggle */}
            <div style={{marginBottom:14}}>
              <div style={{fontSize:11,fontWeight:700,color:'var(--td)',textTransform:'uppercase',letterSpacing:.6,marginBottom:8}}>Code Type</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6}}>
                {[['barcode','📊 Barcode'],['qr','📱 QR Code']].map(([v,l])=>(
                  <button key={v} onClick={()=>setOpts(o=>({...o,codeType:v}))}
                    className={opts.codeType===v?'btn btn-primary btn-sm':'btn btn-ghost btn-sm'}
                    style={{gap:5,fontSize:12}}>
                    {l}
                  </button>
                ))}
              </div>
              {opts.codeType==='qr'&&(
                <div style={{marginTop:8}}>
                  <div style={{fontSize:10,color:'var(--td)',marginBottom:5}}>QR encodes:</div>
                  <select value={opts.qrValue} onChange={e=>setOpts(o=>({...o,qrValue:e.target.value}))}
                    style={{width:'100%',padding:'7px 10px',borderRadius:7,border:'1px solid var(--border)',background:'var(--s2)',color:'var(--text)',fontSize:12}}>
                    {QR_VALUES.map(q=><option key={q.value} value={q.value}>{q.label}</option>)}
                  </select>
                </div>
              )}
            </div>

            {/* Content options */}
            <div style={{marginBottom:14}}>
              <div style={{fontSize:11,fontWeight:700,color:'var(--td)',textTransform:'uppercase',letterSpacing:.6,marginBottom:10}}>Show on Label</div>
              <div style={{display:'flex',flexDirection:'column',gap:9}}>
                <Opt label="Product Name (EN)" k="showName"/>
                <Opt label="Product Name (KH)" k="showNameKh"/>
                <Opt label="SKU Code" k="showSku"/>
                <Opt label="Sale Price" k="showPrice"/>
                <Opt label={opts.codeType==='qr'?'QR Code':'Barcode'} k="showBarcode"/>
              </div>
            </div>

            {/* Summary */}
            <div style={{background:'var(--s2)',borderRadius:8,padding:'10px 12px',fontSize:12,color:'var(--td)'}}>
              <div><span style={{fontWeight:600,color:'var(--text)'}}>{labelItems.length}</span> labels total</div>
              <div>{selected.size} products × {copies} copies</div>
            </div>
          </div>

          {/* ── RIGHT: Preview ── */}
          <div style={{overflowY:'auto',background:'#e5e7eb',padding:16}}>
            <div style={{fontSize:11,color:'#888',marginBottom:10,textAlign:'center'}}>
              Preview — {labelItems.length} label{labelItems.length!==1?'s':''} · {size.label}
            </div>
            {labelItems.length===0?(
              <div style={{textAlign:'center',padding:60,color:'#aaa'}}>
                <Package size={40} style={{margin:'0 auto 12px',opacity:.3}}/>
                <div>Select at least one product</div>
              </div>
            ):(
              <div ref={previewRef} style={{display:'grid',gridTemplateColumns:`repeat(${cols},${size.w}px)`,gap:6,justifyContent:'center'}}>
                {labelItems.map((p,i)=>(
                  <Label key={`${p.id}-${i}`} product={p} opts={opts} size={size}/>
                ))}
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}
